import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Loader2,
  ShieldAlert,
  Navigation,
  Users,
  User,
  Map as MapIcon,
  Check,
  Siren,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { emergencyApi, locationApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

const REASONS = [
  { label: "Chest Pain & Breathlessness", icon: "🫁", color: "#d32f2f" },
  { label: "Accident", icon: "🚗", color: "#e65100" },
  { label: "Pregnancy", icon: "🤰", color: "#7b1fa2" },
  { label: "Fire", icon: "🔥", color: "#ff6f00" },
  { label: "Other", icon: "🆘", color: "#455a64" },
];
const HIGH_PRIORITY = ["Chest Pain & Breathlessness", "Accident", "Pregnancy", "Fire"];

export default function EmergencySOS() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.customerProfile);

  // step: idle -> who -> (location_choice) -> (location_input) -> reason -> confirm
  const [step, setStep] = useState("idle");
  const [forSomeoneElse, setForSomeoneElse] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [coords, setCoords] = useState(null); // {lat,lng,address}
  const [custom, setCustom] = useState({ address: "", landmark: "", latitude: null, longitude: null });
  const [reason, setReason] = useState("");
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState("");
  const [activating, setActivating] = useState(false);

  // autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const sessionToken = useRef(null);
  const debounceRef = useRef(null);

  const resetFlow = () => {
    setStep("idle");
    setForSomeoneElse(false);
    setPatientName("");
    setCoords(null);
    setCustom({ address: "", landmark: "", latitude: null, longitude: null });
    setReason("");
    setLocError("");
    setActivating(false);
    setSuggestions([]);
    setShowSuggest(false);
  };

  const getMyLocation = useCallback(() => {
    setLocating(true);
    setLocError("");
    if (!navigator.geolocation) {
      setLocating(false);
      setStep("location_input");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let address = null;
        try {
          const g = await locationApi.reverseGeocode(lat, lng);
          if (g.data && !g.data.error) {
            address = [g.data.area || g.data.locality, g.data.city, g.data.pincode].filter(Boolean).join(", ") || null;
          }
        } catch { /* ignore */ }
        setCoords({ lat, lng, address });
        setLocating(false);
        setStep("reason");
      },
      () => {
        setLocating(false);
        setLocError("Couldn't get your location. Please enter an address.");
        setStep("location_input");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // Myself
  const chooseMyself = () => {
    setForSomeoneElse(false);
    setPatientName(profile?.basicInfo?.fullName || user?.name || user?.phone || "Self");
    getMyLocation();
  };
  // Someone else
  const chooseSomeoneElse = () => {
    setForSomeoneElse(true);
    setStep("location_choice");
  };

  const fetchSuggestions = async (text) => {
    if (!text || text.length < 3) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }
    setAddrLoading(true);
    try {
      const bias = coords ? { lat: coords.lat, lng: coords.lng } : null;
      const res = await locationApi.autocomplete(text, sessionToken.current, bias);
      if (res.data?.success) {
        setSuggestions(res.data.predictions || []);
        setShowSuggest((res.data.predictions || []).length > 0);
        if (res.data.session_token) sessionToken.current = res.data.session_token;
      }
    } catch {
      setSuggestions([]);
      setShowSuggest(false);
    } finally {
      setAddrLoading(false);
    }
  };

  const onAddressChange = (text) => {
    setCustom((p) => ({ ...p, address: text }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(text), 300);
  };

  const selectSuggestion = async (s) => {
    setCustom((p) => ({ ...p, address: s.description }));
    setShowSuggest(false);
    setSuggestions([]);
    try {
      const res = await locationApi.placeDetails(s.place_id, sessionToken.current);
      if (res.data?.success && res.data.place) {
        const place = res.data.place;
        setCustom((p) => ({
          ...p,
          address: place.formatted_address || s.description,
          latitude: place.latitude || null,
          longitude: place.longitude || null,
        }));
      }
      sessionToken.current = null;
    } catch { /* keep address without coords */ }
  };

  const confirmCustomLocation = () => {
    if (!custom.address.trim()) return;
    setStep("reason");
  };

  const selectReason = (r) => {
    setReason(r);
    setStep("confirm");
  };

  const confirmSOS = async () => {
    setActivating(true);
    try {
      let finalReason = reason || "SOS Emergency - Immediate help needed";
      let location;
      if (forSomeoneElse && custom.latitude && custom.longitude) {
        location = { latitude: custom.latitude, longitude: custom.longitude, address: custom.address || null };
        finalReason = `${reason || "Emergency"} - for someone else${patientName ? ` (${patientName})` : ""}`;
        if (custom.address) {
          finalReason += ` at ${custom.address}`;
          if (custom.landmark) finalReason += `, near ${custom.landmark}`;
        }
      } else if (forSomeoneElse && custom.address) {
        // manual address without coords, fall back to captured coords if any
        location = coords
          ? { latitude: coords.lat, longitude: coords.lng, address: custom.address }
          : null;
        finalReason = `${reason || "Emergency"} - for someone else${patientName ? ` (${patientName})` : ""} at ${custom.address}`;
        if (custom.landmark) finalReason += `, near ${custom.landmark}`;
      } else {
        location = coords ? { latitude: coords.lat, longitude: coords.lng, address: coords.address } : null;
      }

      if (!location) {
        toast.error("Location is required. Please share or enter a location.");
        setActivating(false);
        return;
      }

      const priority = HIGH_PRIORITY.includes(reason) ? "CRITICAL" : "HIGH";

      const res = await emergencyApi.create({
        customerId: user?._id || "",
        customerPhone: user?.phone || "",
        type: "emergency_ambulance",
        priority,
        location,
        reason: finalReason,
        patientInfo: forSomeoneElse
          ? {
              forSomeoneElse: true,
              patientName: patientName || "Unknown",
              customAddress: custom.address || null,
              landmark: custom.landmark || null,
            }
          : null,
        sosData: {
          medicalProfile: {
            patientName: profile?.basicInfo?.fullName || user?.name,
            bloodGroup: profile?.medicalInfo?.bloodGroup,
          },
          capturedAt: new Date().toISOString(),
          deviceInfo: { platform: "web" },
        },
      });
      const c = res.data || {};
      const caseId = c.caseId || c.case?.id || c.case?._id || c.case?.caseId || c.id || c._id;
      if (caseId) {
        toast.success("SOS sent — help is on the way.");
        navigate(`/case-status/${caseId}`);
      } else {
        throw new Error("No case id");
      }
    } catch (e) {
      setActivating(false);
      toast.error(e?.response?.data?.detail || "Unable to reach emergency services. Please call 108/112.");
    }
  };

  const mapsUrl = coords ? `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}` : null;

  return (
    <div className="space-y-6 max-w-2xl">
      <button className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => navigate(-1)} data-testid="sos-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <ShieldAlert className="h-6 w-6 text-destructive" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold">Emergency SOS</h1>
          <p className="text-muted-foreground text-sm">Get immediate medical help.</p>
        </div>
      </div>

      {/* SOS flow card */}
      <Card className="rounded-3xl p-6" data-testid="sos-flow-card">
        <div className="flex flex-col items-center">
          <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
            <Siren className="h-7 w-7 text-destructive siren-pulse" />
          </div>

          {/* IDLE — big SOS trigger */}
          {step === "idle" && (
            <div className="w-full text-center">
              <h2 className="text-xl font-semibold">Trigger Emergency SOS</h2>
              <p className="text-muted-foreground text-sm mt-1">
                Alerts Trata&apos;s emergency network &amp; nearby hospitals with your live location.
              </p>
              <Button
                className="w-full h-14 rounded-2xl text-base mt-5 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                onClick={() => setStep("who")}
                data-testid="start-sos-btn"
              >
                <ShieldAlert className="h-5 w-5" /> Start Emergency SOS
              </Button>
              <p className="text-xs text-muted-foreground mt-3">For immediate danger, always call 108.</p>
            </div>
          )}

          {/* WHO */}
          {step === "who" && (
            <div className="w-full">
              <h2 className="text-xl font-semibold text-center">Emergency Assistance</h2>
              <p className="text-muted-foreground text-sm text-center mt-1">Who needs emergency help?</p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <button onClick={chooseMyself} data-testid="sos-myself-btn" className="rounded-2xl border-2 border-destructive/30 p-5 text-center hover:bg-destructive/5 transition-all">
                  <User className="h-8 w-8 text-destructive mx-auto" />
                  <p className="font-semibold mt-2">Myself</p>
                  <p className="text-xs text-muted-foreground">I need help</p>
                </button>
                <button onClick={chooseSomeoneElse} data-testid="sos-someone-else-btn" className="rounded-2xl border-2 border-border p-5 text-center hover:bg-muted/50 transition-all">
                  <Users className="h-8 w-8 text-foreground mx-auto" />
                  <p className="font-semibold mt-2">Someone Else</p>
                  <p className="text-xs text-muted-foreground">Help another person</p>
                </button>
              </div>
              {locating && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Getting your location…
                </div>
              )}
              <button className="w-full text-center text-sm text-muted-foreground mt-4 py-2" onClick={resetFlow} data-testid="sos-cancel-btn">
                Cancel
              </button>
            </div>
          )}

          {/* LOCATION CHOICE (someone else) */}
          {step === "location_choice" && (
            <div className="w-full">
              <h2 className="text-xl font-semibold text-center">Patient Location</h2>
              <p className="text-muted-foreground text-sm text-center mt-1">Where is the person who needs help?</p>
              <div className="mt-4">
                <label className="text-sm font-medium">Patient Name (Optional)</label>
                <Input
                  className="rounded-xl mt-1"
                  placeholder="Enter patient's name"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  data-testid="patient-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <button onClick={getMyLocation} data-testid="sos-same-location-btn" className="rounded-2xl border-2 border-destructive/30 p-5 text-center hover:bg-destructive/5 transition-all">
                  <MapPin className="h-8 w-8 text-destructive mx-auto" />
                  <p className="font-semibold mt-2">Same Location</p>
                  <p className="text-xs text-muted-foreground">They&apos;re with me</p>
                </button>
                <button onClick={() => setStep("location_input")} data-testid="sos-different-location-btn" className="rounded-2xl border-2 border-border p-5 text-center hover:bg-muted/50 transition-all">
                  <MapIcon className="h-8 w-8 text-foreground mx-auto" />
                  <p className="font-semibold mt-2">Different Location</p>
                  <p className="text-xs text-muted-foreground">I&apos;ll provide address</p>
                </button>
              </div>
              {locating && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mt-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Getting location…
                </div>
              )}
              <button className="w-full text-center text-sm text-muted-foreground mt-4 py-2" onClick={() => setStep("who")}>
                ← Back
              </button>
            </div>
          )}

          {/* LOCATION INPUT (manual) */}
          {step === "location_input" && (
            <div className="w-full">
              <h2 className="text-xl font-semibold text-center">Enter Location</h2>
              <p className="text-muted-foreground text-sm text-center mt-1">Use current location or type an address</p>

              <Button variant="outline" className="w-full rounded-xl mt-4" onClick={getMyLocation} disabled={locating} data-testid="use-current-location-btn">
                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                Use Current Location
              </Button>

              <div className="flex items-center gap-3 my-4">
                <div className="h-px bg-border flex-1" />
                <span className="text-xs text-muted-foreground">OR</span>
                <div className="h-px bg-border flex-1" />
              </div>

              <div className="relative">
                <label className="text-sm font-medium">Address *</label>
                <div className="relative">
                  <Input
                    className="rounded-xl mt-1"
                    placeholder="Start typing address…"
                    value={custom.address}
                    onChange={(e) => onAddressChange(e.target.value)}
                    data-testid="custom-address-input"
                    autoFocus
                  />
                  {addrLoading && <Loader2 className="h-4 w-4 animate-spin text-destructive absolute right-3 top-3.5" />}
                </div>
                {showSuggest && suggestions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-border bg-card shadow-lg max-h-56 overflow-y-auto">
                    {suggestions.map((s, i) => (
                      <button
                        key={s.place_id || i}
                        onClick={() => selectSuggestion(s)}
                        data-testid={`address-suggestion-${i}`}
                        className="flex items-start gap-2 w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors"
                      >
                        <MapPin className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{s.main_text || s.description}</p>
                          {s.secondary_text && <p className="text-xs text-muted-foreground truncate">{s.secondary_text}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {custom.latitude && custom.longitude && (
                  <p className="flex items-center gap-1 text-xs text-success mt-2">
                    <Check className="h-3.5 w-3.5" /> Location coordinates captured
                  </p>
                )}
              </div>

              <div className="mt-3">
                <label className="text-sm font-medium">Landmark (Optional)</label>
                <Input
                  className="rounded-xl mt-1"
                  placeholder="e.g., Near Metro Station, Opposite Park"
                  value={custom.landmark}
                  onChange={(e) => setCustom((p) => ({ ...p, landmark: e.target.value }))}
                  data-testid="custom-landmark-input"
                />
              </div>

              {locError && <p className="text-sm text-destructive mt-2">{locError}</p>}

              <div className="grid grid-cols-2 gap-3 mt-5">
                <Button variant="outline" className="rounded-xl" onClick={() => setStep(forSomeoneElse ? "location_choice" : "who")}>
                  ← Back
                </Button>
                <Button className="rounded-xl bg-destructive hover:bg-destructive/90 text-destructive-foreground" onClick={confirmCustomLocation} disabled={!custom.address.trim()} data-testid="confirm-location-btn">
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* REASON */}
          {step === "reason" && (
            <div className="w-full">
              <h2 className="text-xl font-semibold text-center">What happened?</h2>
              <p className="text-muted-foreground text-sm text-center mt-1">Select the emergency reason to dispatch help</p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                {REASONS.map((r) => (
                  <button
                    key={r.label}
                    onClick={() => selectReason(r.label)}
                    data-testid={`sos-reason-${r.label.split(" ")[0].toLowerCase()}`}
                    className="rounded-2xl border-2 p-5 text-center bg-card hover:shadow-md transition-all"
                    style={{ borderColor: r.color }}
                  >
                    <span className="text-3xl">{r.icon}</span>
                    <p className="font-semibold text-sm mt-2" style={{ color: r.color }}>{r.label}</p>
                  </button>
                ))}
              </div>
              <button className="w-full text-center text-sm text-muted-foreground mt-4 py-2" onClick={resetFlow}>
                Cancel
              </button>
            </div>
          )}

          {/* CONFIRM */}
          {step === "confirm" && (
            <div className="w-full">
              {activating ? (
                <div className="flex flex-col items-center py-6">
                  <Loader2 className="h-10 w-10 animate-spin text-destructive" />
                  <p className="font-semibold mt-4">Dispatching Help…</p>
                  <p className="text-muted-foreground text-sm mt-1 text-center">
                    Creating your request and alerting nearby hospitals.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-center">Confirm Emergency</h2>
                  <div className="flex justify-center mt-3">
                    <span className="inline-flex items-center rounded-full bg-destructive/10 text-destructive font-semibold text-sm px-4 py-1.5">
                      {reason || "Emergency"}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm text-center mt-2">
                    {forSomeoneElse ? `For: ${patientName || "Someone else"}` : "For yourself"}
                  </p>
                  <div className="flex items-start gap-2 rounded-2xl bg-muted/50 p-4 mt-4">
                    <MapPin className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      {custom.address ? (
                        <>
                          <p className="text-sm font-medium">{custom.address}</p>
                          {custom.landmark && <p className="text-xs text-muted-foreground">Near: {custom.landmark}</p>}
                        </>
                      ) : coords ? (
                        <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary">
                          {coords.address || `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`}
                        </a>
                      ) : (
                        <p className="text-sm font-medium">Using current location</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <Button variant="outline" className="rounded-xl" onClick={resetFlow}>
                      Cancel
                    </Button>
                    <Button
                      className="rounded-xl bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                      onClick={confirmSOS}
                      data-testid="confirm-sos-btn"
                    >
                      <ShieldAlert className="h-4 w-4" /> Confirm SOS
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Call 108 — below the SOS trigger */}
      <a href="tel:108" data-testid="call-108" className="flex items-center justify-between rounded-3xl bg-destructive text-destructive-foreground px-6 py-4 hover:opacity-95 transition-opacity">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center">
            <Phone className="h-6 w-6" />
          </div>
          <div>
            <p className="font-semibold text-lg leading-none">Call 108 now</p>
            <p className="text-sm text-destructive-foreground/80 mt-1">National ambulance helpline</p>
          </div>
        </div>
        <Phone className="h-5 w-5" />
      </a>
    </div>
  );
}
